import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  SimpleChanges,
  Type,
  ViewContainerRef,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  debounceTime,
  EMPTY,
  skip,
  startWith,
  Subscription,
  switchMap,
  tap,
  throttleTime,
} from 'rxjs';
import { PaginatorResponse } from '../../models/table.models';
import { WindowService } from '../../services/window.service';
import {
  ACTION_CELL_COMPONENT,
  DETAIL_COMPONENT,
} from '../../token/detail-token';
import { UiAutoSizeDirective } from '../auto-size/auto-size.directive';
import { FeaturesMediaIfDirective } from '@brunsker/libs/core/features/media-if/src';

@Directive({
  selector: '[bsTableOrchestrator]',
  exportAs: 'tableOrchestrator',
})
@UntilDestroy()
export class UiTableOrchestratorDirective<T> implements OnInit, OnChanges, OnDestroy {
  @Input() tableOrchestratorDataSource!: T[];
  @Input() tableOrchestratorPaginatorRef!: MatPaginator;
  @Input() tableOrchestratorPageChanged!: EventEmitter<PageEvent>;
  @Input() tableOrchestratorHasAction!: boolean;
  @Input() tableOrchestratorHasServerSidePagination!: PaginatorResponse<any>;
  @Input() tableOrchestratorColumnsToDisplay!: string[];
  @Input() tableOrchestratorElementKeys!: string[];
  @Input() tableOrchestratorExpandedElementViewMedia!: number;
  @Input() tableOrchestratorMediaIfDirective!: FeaturesMediaIfDirective<number>;
  @Input() tableOrchestratorAutoSize!: QueryList<UiAutoSizeDirective>;
  @Input() tableOrchestratorChangeDetectorRef!: ChangeDetectorRef;
  @Input()
  tableOrchestratorExpandedDetailQueryList!: QueryList<ViewContainerRef>;
  @Input() tableOrchestratorActionQueryList!: QueryList<ViewContainerRef>;
  @Input() tableOrchestratorActionPosition: 'left' | 'right' = 'right';
  @Input() tableOrchestratorEventAction!: EventEmitter<T>;
  @HostBinding('class.expanded-cell') expandedTable!: boolean;
  @HostBinding('class.table-expanded-entry') firstTime = true;

  matTableDataSource!: MatTableDataSource<T>;
  tableColumns!: string[];
  dataKeys!: string[];
  innerWidth: number = window.innerWidth;
  expandedElement!: T | null;
  page!: PageEvent | null;
  paginatorSub?: Subscription;
  subscriptions: Subscription[] = [];

  constructor(
    @Optional()
    @Inject(DETAIL_COMPONENT)
    readonly detailComponentType: Type<
      T & {
        data: T;
        eventAction: EventEmitter<T>;
      }
    >,
    @Optional()
    @Inject(ACTION_CELL_COMPONENT)
    readonly actionCellComponentRef: Type<
      T & {
        data: T;
        eventAction: EventEmitter<T>;
      }
    >,
    readonly changeDetectorRef: ChangeDetectorRef,
    readonly windowService: WindowService
  ) {}

  ngOnInit(): void {
    this.setColumns();

    if (this.tableOrchestratorEventAction) {
      const sub = this.tableOrchestratorEventAction.subscribe(() => {
        this.tableOrchestratorChangeDetectorRef.detectChanges();
      });
      this.subscriptions.push(sub);
    }

    const sub1 = this.windowService.windowSize$.pipe(skip(2)).subscribe(() => {
      this.firstTime = false;
    });
    this.subscriptions.push(sub1);

    this.tableOrchestratorChangeDetectorRef.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['tableOrchestratorColumnsToDisplay'] ||
      changes['tableOrchestratorDataSource']
    ) {
      this.setColumns();
    }

    if (
      (changes['tableOrchestratorPaginatorRef'] ||
        changes['tableOrchestratorHasServerSidePagination']) &&
      this.tableOrchestratorHasServerSidePagination &&
      this.tableOrchestratorPaginatorRef
    ) {
      this.paginatorSub?.unsubscribe();
      this.paginatorSub = this.tableOrchestratorPaginatorRef.page
        .pipe(throttleTime(500))
        .subscribe((page) => {
          sessionStorage.setItem('pageIndex', page.pageIndex.toString());
          if (
            this.page &&
            this.page.pageIndex === page.pageIndex &&
            this.page.pageSize === page.pageSize
          ) {
            return;
          }
          this.page = page;

          this.tableOrchestratorPageChanged.emit(page);
        });
    }

    if (
      this.tableOrchestratorPaginatorRef &&
      this.matTableDataSource.data.length > 0
    ) {
      this.tableOrchestratorPaginatorRef!.pageIndex =
        sessionStorage.getItem('pageIndex') || 0;

      this.matTableDataSource.paginator = this.tableOrchestratorPaginatorRef;

      this.matTableDataSource._updatePaginator(
        this.tableOrchestratorHasServerSidePagination.total
      );
    }

    if (this.tableOrchestratorActionQueryList && this.actionCellComponentRef) {
      this.tableOrchestratorActionQueryList.forEach((element, index) => {
        this.createComponent(element, index, this.actionCellComponentRef);
      });
    }

    if (
      this.tableOrchestratorMediaIfDirective &&
      this.tableOrchestratorMediaIfDirective.onResize$ &&
      this.detailComponentType
    ) {
      this.onResizeUpdate();
    }

    if (
      this.tableOrchestratorAutoSize &&
      this.tableOrchestratorAutoSize.changes &&
      this.actionCellComponentRef
    ) {
      this.setAutoSizeTable();
    }
  }

  ngOnDestroy(): void {
    this.paginatorSub?.unsubscribe();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
  setAutoSizeTable() {
    this.tableOrchestratorAutoSize.changes
      .pipe(
        startWith(this.tableOrchestratorAutoSize),
        untilDestroyed(this),
        tap((tableOrchestratorAutoSize: QueryList<UiAutoSizeDirective>) => {
          tableOrchestratorAutoSize.forEach((elements) => {
            const sub = elements.height$.subscribe(({ height, elementRef }) => {
              if (!height || !elementRef) return;

              const tableCell = elementRef.nativeElement.parentElement;
              const tableRow = tableCell && tableCell.parentElement;

              if (!tableCell || !tableRow) return;

              const elementHeight = height + 30 + 'px';
              tableRow.style.setProperty('--height', elementHeight);
            });
            this.subscriptions.push(sub);
          });
        })
      )
  }

  createComponent(
    viewContainerRef: ViewContainerRef,
    index: number,
    componentType: Type<
      T & {
        data: T;
        eventAction: EventEmitter<T>;
      }
    >
  ) {
    viewContainerRef.clear();
    const nativeElement = viewContainerRef.element.nativeElement;
    nativeElement.remove();
    const componentRef = viewContainerRef.createComponent(componentType);
    componentRef.instance.eventAction = this.tableOrchestratorEventAction;
    componentRef.instance.data = this.tableOrchestratorDataSource[index];
    componentRef.changeDetectorRef.detectChanges();
  }

  setColumns() {
    this.tableOrchestratorChangeDetectorRef?.detectChanges();

    if (!this.tableOrchestratorColumnsToDisplay) return;
    this.tableColumns = this.tableOrchestratorColumnsToDisplay;

    if (this.actionCellComponentRef && this.tableOrchestratorHasAction) {
      this.tableOrchestratorHasAction = true;
    }

    if (
      this.innerWidth <= this.tableOrchestratorExpandedElementViewMedia &&
      this.tableOrchestratorExpandedElementViewMedia > 0
    ) {
      this.tableOrchestratorHasAction = false;
    }

    if (this.tableOrchestratorHasAction) {
      const data =
        this.tableOrchestratorActionPosition === 'right'
          ? [...this.tableColumns, 'actions']
          : [...['actions'], ...this.tableColumns];
      this.tableColumns = data;
    }

    if (
      !this.tableOrchestratorDataSource ||
      this.tableOrchestratorDataSource.length <= 0
    ) {
      throw new Error('DataSource is empty');
    }

    this.dataKeys =
      this.tableOrchestratorElementKeys ||
      Object.keys(this.tableOrchestratorDataSource[0] as Object);
    this.matTableDataSource = new MatTableDataSource<T>(
      this.tableOrchestratorDataSource
    );

    if (!!this.tableOrchestratorPaginatorRef) {
      this.matTableDataSource.paginator = this.tableOrchestratorPaginatorRef;
    }

    if (this.tableOrchestratorPaginatorRef) {
      this.matTableDataSource.paginator = this.tableOrchestratorPaginatorRef;
    }

    this.tableOrchestratorChangeDetectorRef.detectChanges();
  }

  onResizeUpdate(): void {
    const sub = this.tableOrchestratorMediaIfDirective.onResize$
      .pipe(
        startWith(window.innerWidth),
        debounceTime(60),
        switchMap(() => {
          this.innerWidth = window.innerWidth;
          this.expandedTable = true;

          this.setColumns();

          if (
            this.tableOrchestratorHasAction &&
            this.tableOrchestratorActionQueryList
          ) {
            this.expandedTable = false;

            return this.tableOrchestratorActionQueryList.changes.pipe(
              startWith(this.tableOrchestratorActionQueryList),
              throttleTime(60),
              untilDestroyed(this),
              tap((updateQueryList: QueryList<ViewContainerRef>) => {
                updateQueryList.forEach((viewContainerRef, index) => {
                  this.createComponent(
                    viewContainerRef,
                    index,
                    this.actionCellComponentRef
                  );
                });
              })
            );
          }

          if (this.tableOrchestratorExpandedDetailQueryList) {
            return this.tableOrchestratorExpandedDetailQueryList.changes.pipe(
              startWith(this.tableOrchestratorExpandedDetailQueryList),
              untilDestroyed(this),
              throttleTime(60),
              tap((updateQueryList: QueryList<ViewContainerRef>) => {
                updateQueryList.forEach((viewContainerRef, index) => {
                  this.createComponent(
                    viewContainerRef,
                    index,
                    this.detailComponentType
                  );
                });
              })
            );
          }

          return EMPTY;
        })
      )
      .subscribe(() => {
        this.tableOrchestratorChangeDetectorRef.detectChanges();
      });
    this.subscriptions.push(sub);
  }
}
